import { useEffect, useMemo, useState } from "react";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import { usePayments } from "../../../hooks/usePayments";
import { useIncomeExpense } from "../../../hooks/useIncomeExpense";
import { useUsers } from "../../../hooks/useUsers";
import { useSharePresales } from "../../../hooks/useSharePresales";
import {
  getDeliveryAssignments,
  rehydrateDeliveryAssignments,
  subscribeDeliveryAssignments,
  type DeliveryAssignment,
} from "../../../lib/deliveryStore";
import {
  getSlaughterOps,
  rehydrateSlaughterOps,
  subscribeSlaughterOps,
  type SlaughterOpRecord,
} from "../../../lib/slaughterOpsStore";
import {
  aggregateReports,
  createDefaultReportFilters,
  type ReportFilters,
  type ReportQuickPreset,
} from "../../../lib/reportAggregations";
import {
  getReportPrintSettings,
  subscribeReportPrintSettings,
  updateReportPrintSettings,
} from "../../../lib/reportPrintSettings";
import {
  downloadAnimalWorkbook,
  downloadCustomerWorkbook,
  downloadFinanceWorkbook,
  downloadManagementWorkbook,
  downloadUserWorkbook,
  downloadAllReportsWorkbook,
  downloadSharePresaleWorkbook,
} from "../../../lib/reportWorkbook";

function fmtMoney(value: number) {
  return value.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

function fmtNumber(value: number) {
  return value.toLocaleString("tr-TR");
}

function SectionCard({
  title,
  subtitle,
  children,
  actions,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <section className="rounded-[20px] border border-farm-border bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-farm-border px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-farm-brown">{title}</h2>
          {subtitle && (
            <p className="mt-1 text-xs text-farm-muted">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint: string;
  icon: string;
}) {
  return (
    <div className="rounded-[18px] border border-farm-border bg-gradient-to-br from-farm-warm via-white to-farm-earth-pale/40 px-4 py-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-farm-muted/80">
            {label}
          </p>
          <p className="mt-3 font-heading text-2xl font-bold text-farm-brown">
            {value}
          </p>
          <p className="mt-2 text-xs text-farm-muted">{hint}</p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-farm-earth text-white shadow-sm">
          <i className={`${icon} text-xl`} />
        </div>
      </div>
    </div>
  );
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${
        active
          ? "border-farm-earth bg-farm-earth text-white"
          : "border-farm-border bg-farm-warm text-farm-brown hover:bg-farm-earth-pale"
      }`}
    >
      {children}
    </button>
  );
}

function InputLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-farm-muted/80">
      {children}
    </label>
  );
}

function FilterInput(
  props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }
) {
  const { label, className = "", ...rest } = props;
  return (
    <div className="space-y-1.5">
      <InputLabel>{label}</InputLabel>
      <input
        {...rest}
        className={`h-10 w-full rounded-xl border border-farm-border bg-farm-warm px-3 text-sm text-farm-brown outline-none transition-colors placeholder:text-farm-muted/60 focus:border-farm-earth ${className}`}
      />
    </div>
  );
}

function FilterSelect(
  props: React.SelectHTMLAttributes<HTMLSelectElement> & {
    label: string;
    options: Array<{ value: string; label: string }>;
  }
) {
  const { label, options, className = "", ...rest } = props;
  return (
    <div className="space-y-1.5">
      <InputLabel>{label}</InputLabel>
      <select
        {...rest}
        className={`h-10 w-full rounded-xl border border-farm-border bg-farm-warm px-3 text-sm text-farm-brown outline-none focus:border-farm-earth ${className}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function MiniBar({ label, value, total }: { label: string; value: number; total: number }) {
  const ratio = total > 0 ? Math.max(8, Math.round((value / total) * 100)) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="font-medium text-farm-brown">{label}</span>
        <span className="text-farm-muted">{fmtNumber(value)}</span>
      </div>
      <div className="h-2 rounded-full bg-farm-earth-pale">
        <div
          className="h-2 rounded-full bg-farm-earth"
          style={{ width: `${Math.min(100, ratio)}%` }}
        />
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-farm-border bg-farm-warm px-4 py-10 text-center text-sm text-farm-muted">
      {message}
    </div>
  );
}

function ReportTable({
  headers,
  children,
}: {
  headers: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[18px] border border-farm-border">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-farm-border text-sm">
          <thead className="bg-farm-brown text-white">
            <tr>
              {headers.map((header) => (
                <th key={header} className="whitespace-nowrap px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em]">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-farm-border bg-white">{children}</tbody>
        </table>
      </div>
    </div>
  );
}

const TAB_ITEMS = [
  { key: "overview", label: "Genel Bakış", icon: "ri-dashboard-horizontal-line" },
  { key: "sales", label: "Satış", icon: "ri-price-tag-3-line" },
  { key: "finance", label: "Finans", icon: "ri-wallet-3-line" },
  { key: "operations", label: "Operasyon", icon: "ri-truck-line" },
  { key: "customers", label: "Müşteri", icon: "ri-group-line" },
  { key: "users", label: "Kullanıcı", icon: "ri-user-settings-line" },
] as const;

type TabKey = (typeof TAB_ITEMS)[number]["key"];

export default function RaporlarPage() {
  const { animals } = useAnimals();
  const { customers } = useCustomers();
  const { payments } = usePayments();
  const { entries } = useIncomeExpense();
  const { users } = useUsers();
  const { sharePresales } = useSharePresales();

  const [deliveries, setDeliveries] = useState<DeliveryAssignment[]>(() =>
    getDeliveryAssignments()
  );
  const [slaughterOps, setSlaughterOps] = useState<SlaughterOpRecord[]>(() =>
    getSlaughterOps()
  );
  const [filters, setFilters] = useState<ReportFilters>(createDefaultReportFilters);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [printSettings, setPrintSettings] = useState(getReportPrintSettings);

  useEffect(() => {
    void rehydrateDeliveryAssignments();
    void rehydrateSlaughterOps();

    const unsubDeliveries = subscribeDeliveryAssignments(() => {
      setDeliveries(getDeliveryAssignments());
    });
    const unsubSlaughter = subscribeSlaughterOps(() => {
      setSlaughterOps(getSlaughterOps());
    });
    const unsubPrint = subscribeReportPrintSettings(() => {
      setPrintSettings(getReportPrintSettings());
    });

    setDeliveries(getDeliveryAssignments());
    setSlaughterOps(getSlaughterOps());

    return () => {
      unsubDeliveries();
      unsubSlaughter();
      unsubPrint();
    };
  }, []);

  const report = useMemo(
    () =>
      aggregateReports({
        animals,
        customers,
        payments,
        incomeExpenseEntries: entries,
        users,
        deliveries,
        slaughterOps,
        filters,
      }),
    [animals, customers, payments, entries, users, deliveries, slaughterOps, filters]
  );

  const maxSalesTypeCount = Math.max(
    1,
    ...report.salesByType.map((item) => item.value)
  );

  function updateFilter<K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function applyPreset(preset: ReportQuickPreset) {
    const next = createDefaultReportFilters();
    next.preset = preset;
    if (preset === "today-sales" || preset === "today-payments") {
      const today = new Date().toISOString().slice(0, 10);
      next.dateFrom = today;
      next.dateTo = today;
    }
    if (preset === "debtors") {
      next.paymentStatus = "Borçlu";
    }
    if (preset === "transport-awaiting") {
      next.paymentStatus = "Tümü";
    }
    setFilters(next);
  }

  return (
    <div className="min-h-screen bg-farm-cream px-4 py-5 md:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5">
        <div className="rounded-[24px] border border-farm-border bg-gradient-to-r from-farm-brown via-[#5b3f2f] to-farm-earth px-5 py-5 text-white shadow-lg md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/90">
                <i className="ri-bar-chart-box-line" />
                Raporlar + Yazdırma Merkezi
              </div>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight md:text-4xl">
                Gerçek yönetim ekranı burada başlıyor.
              </h1>
              <p className="mt-3 max-w-2xl text-sm text-white/80 md:text-base">
                Satış, finans, operasyon, müşteri ve kullanıcı verilerini tek merkezde
                özetle. Panel rengine uyumlu dashboard ile filtrele, yaklaşan XLSX ve
                yazdırma akışını buradan yönet.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:min-w-[520px]">
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/65">Toplam Satış</p>
                <p className="mt-2 text-lg font-bold">{fmtMoney(report.overview.totalSale)}</p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/65">Tahsilat</p>
                <p className="mt-2 text-lg font-bold">{fmtMoney(report.overview.totalPaid)}</p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/65">Kalan</p>
                <p className="mt-2 text-lg font-bold">{fmtMoney(report.overview.totalRemaining)}</p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/65">Kâr / Zarar</p>
                <p className="mt-2 text-lg font-bold">{fmtMoney(report.overview.profit)}</p>
              </div>
            </div>
          </div>
        </div>

        <SectionCard
          title="Global Filtre Merkezi"
          subtitle="Bir kere filtrele, tüm dashboard kartları aynı veriyi kullansın."
          actions={
            <>
              <PillButton active={filters.preset === "today-sales"} onClick={() => applyPreset("today-sales")}>Bugünkü Satışlar</PillButton>
              <PillButton active={filters.preset === "today-payments"} onClick={() => applyPreset("today-payments")}>Bugünkü Tahsilatlar</PillButton>
              <PillButton active={filters.preset === "debtors"} onClick={() => applyPreset("debtors")}>Borçlu Müşteriler</PillButton>
              <PillButton active={filters.preset === "unsold-animals"} onClick={() => applyPreset("unsold-animals")}>Satışsız Hayvanlar</PillButton>
              <PillButton onClick={() => setFilters(createDefaultReportFilters())}>Sıfırla</PillButton>
            </>
          }
        >
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <FilterInput label="Genel Arama" value={filters.search} onChange={(e) => updateFilter("search", e.target.value)} placeholder="Müşteri, not, fiş, hisse..." />
            <FilterInput label="Küpe No" value={filters.kupeNo} onChange={(e) => updateFilter("kupeNo", e.target.value)} placeholder="Tam eşleşme" />
            <FilterInput label="Müşteri" value={filters.customer} onChange={(e) => updateFilter("customer", e.target.value)} placeholder="Ad soyad ara" />
            <FilterInput label="Kullanıcı" value={filters.user} onChange={(e) => updateFilter("user", e.target.value)} placeholder="Ödeme alan kullanıcı" />
            <FilterSelect
              label="Satış Tipi"
              value={filters.salesType}
              onChange={(e) => updateFilter("salesType", e.target.value as ReportFilters["salesType"])}
              options={[
                { value: "Tümü", label: "Tümü" },
                { value: "Hisseli", label: "Hisseli" },
                { value: "Hissesiz", label: "Hissesiz" },
                { value: "Dış Kesim", label: "Dış Kesim" },
              ]}
            />
            <FilterSelect
              label="Ödeme Durumu"
              value={filters.paymentStatus}
              onChange={(e) => updateFilter("paymentStatus", e.target.value as ReportFilters["paymentStatus"])}
              options={[
                { value: "Tümü", label: "Tümü" },
                { value: "Ödendi", label: "Tam Ödendi" },
                { value: "Borçlu", label: "Borçlu" },
                { value: "Eksi Bakiye", label: "Eksi Bakiye" },
              ]}
            />
            <FilterSelect
              label="Not Durumu"
              value={filters.noteMode}
              onChange={(e) => updateFilter("noteMode", e.target.value as ReportFilters["noteMode"])}
              options={[
                { value: "Tümü", label: "Tümü" },
                { value: "Not Var", label: "Not Var" },
                { value: "Not Yok", label: "Not Yok" },
              ]}
            />
            <FilterInput label="Başlangıç Tarihi" type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)} />
            <FilterInput label="Bitiş Tarihi" type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)} />
            <FilterInput label="Min Satış" value={filters.minSale} onChange={(e) => updateFilter("minSale", e.target.value)} placeholder="0" />
            <FilterInput label="Max Satış" value={filters.maxSale} onChange={(e) => updateFilter("maxSale", e.target.value)} placeholder="250000" />
            <FilterInput label="Min Kalan" value={filters.minRemaining} onChange={(e) => updateFilter("minRemaining", e.target.value)} placeholder="0" />
            <FilterInput label="Max Kalan" value={filters.maxRemaining} onChange={(e) => updateFilter("maxRemaining", e.target.value)} placeholder="250000" />
          </div>
        </SectionCard>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Toplam Hayvan" value={fmtNumber(report.overview.totalAnimals)} hint="Filtreye giren toplam hayvan" icon="ri-database-2-line" />
          <StatCard label="Satılan Hisse" value={`${fmtNumber(report.overview.soldShares)} / ${fmtNumber(report.overview.totalShares)}`} hint="Dolu ve boş hisse dengesi" icon="ri-pie-chart-2-line" />
          <StatCard label="Toplam Tahsilat" value={fmtMoney(report.overview.totalPaid)} hint={`${fmtNumber(report.paymentSummary.paymentCount)} ödeme kaydı`} icon="ri-coins-line" />
          <StatCard label="Taşıma Bekleyen" value={fmtNumber(report.overview.transportWaiting)} hint={`${fmtNumber(report.overview.deliveredCount)} teslim tamamlandı`} icon="ri-truck-line" />
        </div>

        <div className="flex flex-wrap gap-2">
          {TAB_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setActiveTab(item.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                activeTab === item.key
                  ? "border-farm-earth bg-farm-earth text-white"
                  : "border-farm-border bg-white text-farm-brown hover:bg-farm-warm"
              }`}
            >
              <i className={item.icon} />
              {item.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <div className="grid gap-5 xl:grid-cols-[1.3fr_0.9fr]">
            <SectionCard title="Genel Bakış" subtitle="Satış, finans ve operasyon özetleri tek bakışta.">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-farm-border bg-farm-warm px-4 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-farm-muted/70">Toplam Satış</p>
                  <p className="mt-2 text-xl font-bold text-farm-brown">{fmtMoney(report.overview.totalSale)}</p>
                </div>
                <div className="rounded-2xl border border-farm-border bg-farm-warm px-4 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-farm-muted/70">Toplam Kalan</p>
                  <p className="mt-2 text-xl font-bold text-farm-brown">{fmtMoney(report.overview.totalRemaining)}</p>
                </div>
                <div className="rounded-2xl border border-farm-border bg-farm-warm px-4 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-farm-muted/70">Bugünkü Tahsilat</p>
                  <p className="mt-2 text-xl font-bold text-farm-brown">{fmtMoney(report.paymentSummary.todayAmount)}</p>
                </div>
                <div className="rounded-2xl border border-farm-border bg-farm-warm px-4 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-farm-muted/70">Kâr / Zarar</p>
                  <p className="mt-2 text-xl font-bold text-farm-brown">{fmtMoney(report.overview.profit)}</p>
                </div>
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl border border-farm-border bg-white p-4">
                  <h3 className="text-sm font-bold text-farm-brown">Satış tipi dağılımı</h3>
                  <div className="mt-4 space-y-3">
                    {report.salesByType.map((item) => (
                      <MiniBar key={item.label} label={`${item.label} • ${fmtMoney(item.amount)}`} value={item.value} total={maxSalesTypeCount} />
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-farm-border bg-white p-4">
                  <h3 className="text-sm font-bold text-farm-brown">Operasyon nabzı</h3>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl bg-farm-earth-pale px-3 py-3">
                      <p className="text-farm-muted">Kesim Bekleyen</p>
                      <p className="mt-1 text-lg font-bold text-farm-brown">{fmtNumber(report.operationSummary.queued)}</p>
                    </div>
                    <div className="rounded-xl bg-farm-earth-pale px-3 py-3">
                      <p className="text-farm-muted">Parçalamada</p>
                      <p className="mt-1 text-lg font-bold text-farm-brown">{fmtNumber(report.operationSummary.packing)}</p>
                    </div>
                    <div className="rounded-xl bg-farm-earth-pale px-3 py-3">
                      <p className="text-farm-muted">Taşıma Halinde</p>
                      <p className="mt-1 text-lg font-bold text-farm-brown">{fmtNumber(report.operationSummary.onDelivery)}</p>
                    </div>
                    <div className="rounded-xl bg-farm-earth-pale px-3 py-3">
                      <p className="text-farm-muted">Teslim Edilen</p>
                      <p className="mt-1 text-lg font-bold text-farm-brown">{fmtNumber(report.operationSummary.delivered)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>

            <div className="space-y-5">
              <SectionCard title="Excel Merkezi" subtitle="CSV değil, Excel uyumlu çok sayfalı tablo raporları buradan alınır.">
                <div className="rounded-2xl border border-dashed border-farm-border bg-farm-earth-pale/35 px-4 py-3 text-xs text-farm-muted">
                  Bu sürümde exportlar Excel uyumlu çok sayfalı workbook olarak <strong>.xls</strong> iner. CSV gibi tek satıra yığılmaz; başlık, sheet ve tablo düzeni korunur.
                </div>

                <div className="mt-4 rounded-2xl border border-farm-border bg-farm-warm px-4 py-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="font-semibold text-farm-brown">Tüm Raporlar Tek Excel</p>
                      <p className="mt-1 text-xs text-farm-muted">Yönetim, hayvan, hisse detayları, müşteri, ödeme, finans ve kullanıcı sayfalarını tek workbook içinde indirir.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => downloadAllReportsWorkbook(report, filters, report.topUsers, sharePresales.sales)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-farm-brown px-3.5 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                    >
                      <i className="ri-file-excel-2-line" />
                      Tek Excel İndir
                    </button>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {([
                    ["Yönetim Raporu", "Özet + satış + tahsilat + operasyon", () => downloadManagementWorkbook(report, filters)],
                    ["Hayvan Raporu", "Ana liste + hisse detayları + satış dağılımı", () => downloadAnimalWorkbook(report, filters)],
                    ["Hisseli Satış Raporu", "Hisse ön satış listesi + grup + kapora", () => downloadSharePresaleWorkbook(sharePresales.sales, filters)],
                    ["Müşteri Raporu", "Müşteri satışları + ödemeler + borç", () => downloadCustomerWorkbook(report, filters)],
                    ["Finans Raporu", "Gelir + gider + kalan bakiye + kâr/zarar", () => downloadFinanceWorkbook(report, filters)],
                    ["Kullanıcı Raporu", "Rol ve performans özeti", () => downloadUserWorkbook(report.topUsers, filters)],
                  ] as Array<[string, string, () => void]>).map(([title, text, onClick]) => (
                    <div key={String(title)} className="rounded-2xl border border-farm-border bg-farm-warm px-4 py-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="font-semibold text-farm-brown">{title}</p>
                          <p className="mt-1 text-xs text-farm-muted">{text}</p>
                        </div>

                        <button
                          type="button"
                          onClick={onClick as () => void}
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-farm-earth px-3.5 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                        >
                          <i className="ri-file-excel-2-line" />
                          Excel'e İndir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Yazdırma Ayarları" subtitle="Satış ve ödeme özet fişlerinin davranışını buradan yönet.">
                <div className="space-y-4">
                  <label className="flex items-center justify-between rounded-2xl border border-farm-border bg-farm-warm px-4 py-3">
                    <div>
                      <p className="font-semibold text-farm-brown">Satış çıktısı otomatik açılsın</p>
                      <p className="text-xs text-farm-muted">Kayıt sonrası print taslağı açılır.</p>
                    </div>
                    <input type="checkbox" checked={printSettings.saleAutoPrint} onChange={(e) => updateReportPrintSettings({ saleAutoPrint: e.target.checked })} className="h-5 w-5 accent-[#B8865A]" />
                  </label>

                  <label className="flex items-center justify-between rounded-2xl border border-farm-border bg-farm-warm px-4 py-3">
                    <div>
                      <p className="font-semibold text-farm-brown">Ödeme çıktısı otomatik açılsın</p>
                      <p className="text-xs text-farm-muted">Ödeme sonrası print taslağı açılır.</p>
                    </div>
                    <input type="checkbox" checked={printSettings.paymentAutoPrint} onChange={(e) => updateReportPrintSettings({ paymentAutoPrint: e.target.checked })} className="h-5 w-5 accent-[#B8865A]" />
                  </label>

                  <FilterSelect
                    label="Kağıt Boyutu"
                    value={printSettings.paperSize}
                    onChange={(e) => updateReportPrintSettings({ paperSize: e.target.value as "Yarım A4" | "Tam A4" })}
                    options={[
                      { value: "Yarım A4", label: "Yarım A4" },
                      { value: "Tam A4", label: "Tam A4" },
                    ]}
                  />

                  <div className="space-y-1.5">
                    <InputLabel>Satış Alt Mesajı</InputLabel>
                    <textarea
                      value={printSettings.saleFooterMessage}
                      onChange={(e) => updateReportPrintSettings({ saleFooterMessage: e.target.value })}
                      rows={3}
                      className="w-full rounded-2xl border border-farm-border bg-farm-warm px-3 py-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <InputLabel>Ödeme Alt Mesajı</InputLabel>
                    <textarea
                      value={printSettings.paymentFooterMessage}
                      onChange={(e) => updateReportPrintSettings({ paymentFooterMessage: e.target.value })}
                      rows={3}
                      className="w-full rounded-2xl border border-farm-border bg-farm-warm px-3 py-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
                    />
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        )}

        {activeTab === "sales" && (
          <SectionCard title="Satış Dashboard" subtitle="Hayvan bazlı ve hisse bazlı satış görünümü.">
            {report.filteredSales.length === 0 ? (
              <EmptyState message="Filtreye uyan satış kaydı bulunamadı." />
            ) : (
              <ReportTable headers={["Müşteri", "Küpe", "Sipariş", "Hisse", "Tip", "Satış", "Ödenen", "Kalan", "Satış Notu"]}>
                {report.filteredSales.slice(0, 24).map((row) => (
                  <tr key={`${row.animalId}-${row.shareId}`} className="hover:bg-farm-warm/70">
                    <td className="px-4 py-3 text-farm-brown">{row.customerName}</td>
                    <td className="px-4 py-3 text-farm-brown">{row.kupeNo}</td>
                    <td className="px-4 py-3 text-farm-muted">{row.siparisNo}</td>
                    <td className="px-4 py-3 text-farm-muted">{row.shareNo}</td>
                    <td className="px-4 py-3"><span className="rounded-full bg-farm-earth-pale px-2 py-1 text-[11px] font-semibold text-farm-earth">{row.saleType}</span></td>
                    <td className="px-4 py-3 text-farm-brown">{fmtMoney(row.saleAmount)}</td>
                    <td className="px-4 py-3 text-green-700">{fmtMoney(row.paidAmount)}</td>
                    <td className={`px-4 py-3 font-semibold ${row.remainingAmount > 0 ? "text-red-600" : "text-farm-brown"}`}>{fmtMoney(row.remainingAmount)}</td>
                    <td className="px-4 py-3 text-farm-muted">{row.note || "-"}</td>
                  </tr>
                ))}
              </ReportTable>
            )}
          </SectionCard>
        )}

        {activeTab === "finance" && (
          <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
            <SectionCard title="Finans Özeti" subtitle="Tahsilat, kalan bakiye ve ödeme yoğunluğu.">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Tahsilat" value={fmtMoney(report.overview.totalPaid)} hint="Toplam tahsil edilmiş tutar" icon="ri-coins-line" />
                <StatCard label="Borçlu Müşteri" value={fmtNumber(report.paymentSummary.debtors)} hint="Kalan tutarı sıfırdan büyük olanlar" icon="ri-alert-line" />
                <StatCard label="Eksi Bakiye" value={fmtNumber(report.paymentSummary.negativeBalanceCount)} hint="Kapora fazlası kalan kayıtlar" icon="ri-error-warning-line" />
                <StatCard label="Bugünkü Ödeme" value={fmtMoney(report.paymentSummary.todayAmount)} hint="Bugün girilmiş tahsilatlar" icon="ri-calendar-check-line" />
              </div>

              <div className="mt-5">
                {report.filteredPayments.length === 0 ? (
                  <EmptyState message="Filtreye uyan ödeme kaydı yok." />
                ) : (
                  <ReportTable headers={["Tarih", "Müşteri", "Küpe", "Fiş", "Tip", "Tutar", "Kullanıcı"]}>
                    {report.filteredPayments.slice(0, 20).map((payment) => (
                      <tr key={payment.id} className="hover:bg-farm-warm/70">
                        <td className="px-4 py-3 text-farm-muted">{String(payment.tarih).slice(0, 10)}</td>
                        <td className="px-4 py-3 text-farm-brown">{payment.musteriAdi}</td>
                        <td className="px-4 py-3 text-farm-muted">{payment.hayvanKupeNo}</td>
                        <td className="px-4 py-3 text-farm-muted">{payment.fisNo || "-"}</td>
                        <td className="px-4 py-3 text-farm-muted">{payment.tip}</td>
                        <td className="px-4 py-3 font-semibold text-green-700">{fmtMoney(Number(payment.tutar ?? 0))}</td>
                        <td className="px-4 py-3 text-farm-muted">{payment.olusturan}</td>
                      </tr>
                    ))}
                  </ReportTable>
                )}
              </div>
            </SectionCard>

            <SectionCard title="En Yüksek Kalan Bakiye" subtitle="Borç yükü en yüksek müşteriler.">
              {report.topCustomers.length === 0 ? (
                <EmptyState message="Gösterilecek müşteri özeti bulunamadı." />
              ) : (
                <div className="space-y-3">
                  {report.topCustomers.map((row) => (
                    <div key={row.customerId} className="rounded-2xl border border-farm-border bg-farm-warm px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-farm-brown">{row.customerName}</p>
                          <p className="text-xs text-farm-muted">{row.phone} • {fmtNumber(row.shareCount)} hisse</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-farm-muted">Kalan</p>
                          <p className={`font-bold ${row.totalRemaining > 0 ? "text-red-600" : "text-farm-brown"}`}>{fmtMoney(row.totalRemaining)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {activeTab === "operations" && (
          <SectionCard title="Operasyon Dashboard" subtitle="Kesim ve taşıma yoğunluğunu tek ekranda izle.">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Kesim Bekleyen" value={fmtNumber(report.operationSummary.queued)} hint="Sıraya alınan hayvan" icon="ri-scissors-cut-line" />
              <StatCard label="Parçalamada" value={fmtNumber(report.operationSummary.packing)} hint="Packing alanındaki kayıt" icon="ri-box-3-line" />
              <StatCard label="Yola Hazır" value={fmtNumber(report.operationSummary.readyForDelivery)} hint="Taşıyıcı ataması bekleyen/ hazır" icon="ri-route-line" />
              <StatCard label="Teslim Edilen" value={fmtNumber(report.operationSummary.delivered)} hint="Teslim süreci kapanan kayıt" icon="ri-checkbox-circle-line" />
            </div>

            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <div className="rounded-2xl border border-farm-border bg-farm-warm p-4">
                <h3 className="text-sm font-bold text-farm-brown">Kesim akışı</h3>
                <div className="mt-4 space-y-3">
                  <MiniBar label="Queued" value={report.operationSummary.queued} total={Math.max(1, report.overview.totalAnimals)} />
                  <MiniBar label="Entered" value={report.operationSummary.entered} total={Math.max(1, report.overview.totalAnimals)} />
                  <MiniBar label="Cut" value={report.operationSummary.cut} total={Math.max(1, report.overview.totalAnimals)} />
                  <MiniBar label="Packing" value={report.operationSummary.packing} total={Math.max(1, report.overview.totalAnimals)} />
                  <MiniBar label="Done" value={report.operationSummary.done} total={Math.max(1, report.overview.totalAnimals)} />
                </div>
              </div>

              <div className="rounded-2xl border border-farm-border bg-farm-warm p-4">
                <h3 className="text-sm font-bold text-farm-brown">Taşıma akışı</h3>
                <div className="mt-4 space-y-3">
                  <MiniBar label="Hazır" value={report.operationSummary.readyForDelivery} total={Math.max(1, report.operationSummary.readyForDelivery + report.operationSummary.onDelivery + report.operationSummary.delivered)} />
                  <MiniBar label="Taşıma Halinde" value={report.operationSummary.onDelivery} total={Math.max(1, report.operationSummary.readyForDelivery + report.operationSummary.onDelivery + report.operationSummary.delivered)} />
                  <MiniBar label="Teslim Edildi" value={report.operationSummary.delivered} total={Math.max(1, report.operationSummary.readyForDelivery + report.operationSummary.onDelivery + report.operationSummary.delivered)} />
                </div>
              </div>
            </div>
          </SectionCard>
        )}

        {activeTab === "customers" && (
          <SectionCard title="Müşteri Dashboard" subtitle="Müşteri bazlı alış, ödeme ve kalan raporu.">
            {report.filteredCustomers.length === 0 ? (
              <EmptyState message="Filtreye uyan müşteri raporu bulunamadı." />
            ) : (
              <ReportTable headers={["Müşteri", "Telefon", "Toplam Satış", "Ödenen", "Kalan", "Hisse", "Not Sayısı"]}>
                {report.filteredCustomers.slice(0, 24).map((row) => (
                  <tr key={row.customerId} className="hover:bg-farm-warm/70">
                    <td className="px-4 py-3 text-farm-brown">{row.customerName}</td>
                    <td className="px-4 py-3 text-farm-muted">{row.phone}</td>
                    <td className="px-4 py-3 text-farm-brown">{fmtMoney(row.totalSale)}</td>
                    <td className="px-4 py-3 text-green-700">{fmtMoney(row.totalPaid)}</td>
                    <td className={`px-4 py-3 font-semibold ${row.totalRemaining > 0 ? "text-red-600" : "text-farm-brown"}`}>{fmtMoney(row.totalRemaining)}</td>
                    <td className="px-4 py-3 text-farm-muted">{fmtNumber(row.shareCount)}</td>
                    <td className="px-4 py-3 text-farm-muted">{fmtNumber(row.noteCount)}</td>
                  </tr>
                ))}
              </ReportTable>
            )}
          </SectionCard>
        )}

        {activeTab === "users" && (
          <SectionCard title="Kullanıcı Dashboard" subtitle="Ödeme alan ve kayıt oluşturan ekip görünümü.">
            {report.topUsers.length === 0 ? (
              <EmptyState message="Kullanıcı performans özeti için veri bulunamadı." />
            ) : (
              <ReportTable headers={["Kullanıcı", "Rol", "Oluşturulan Müşteri", "Ödeme Adedi", "Ödeme Tutarı"]}>
                {report.topUsers.map((row) => (
                  <tr key={`${row.userName}-${row.role}`} className="hover:bg-farm-warm/70">
                    <td className="px-4 py-3 text-farm-brown">{row.userName}</td>
                    <td className="px-4 py-3 text-farm-muted">{row.role}</td>
                    <td className="px-4 py-3 text-farm-muted">{fmtNumber(row.createdCustomers)}</td>
                    <td className="px-4 py-3 text-farm-muted">{fmtNumber(row.paymentCount)}</td>
                    <td className="px-4 py-3 text-green-700">{fmtMoney(row.paymentAmount)}</td>
                  </tr>
                ))}
              </ReportTable>
            )}
          </SectionCard>
        )}
      </div>
    </div>
  );
}
